import React from 'react';
import './Research.css';

interface ResearchPaper {
  title: string;
  authors: string[];
  abstract: string;
  date: string;
  links: {
    paper?: string;
    github?: string;
    huggingface?: string;
    modelSeries?: {
      name: string;
      url: string;
    };
    report?: string;
  };
  tags: string[];
}

const researchPapers: ResearchPaper[] = [
  {
    title: "EasyMath: A 0-shot Math Benchmark for SLMs",
    authors: ["Drishya Karki", "Michiel Kamphuis", "Angelecia Frey"],
    abstract: "EasyMath is a compact benchmark for practical math reasoning in small language models. It covers thirteen categories, from basic arithmetic and order of operations to word problems, algebraic expressions, edge cases, and omits specialist topics. We tested 23 models (14M to 4B parameters) using exact, numerical, and symbolic checks on free-form answers in a zero-shot setting. Accuracy rises with size and training, chain-of-thought adds modest gains, and consistency improves at scale.",
    date: "2025-05",
    links: {
      paper: "https://arxiv.org/abs/2505.14852"
    },
    tags: ["Math", "Benchmark", "SLMs"]
  },
  {
    title: "SmolLM2 - Humanized models",
    authors: ["AssistantsLab Team"],
    abstract: "SmolLM2-Humanized models are finetuned versions of the SmolLM2 Series models. SmolLM2 is a family of compact language models available in three size: 135M, 360M, and 1.7B parameters. They are capable of solving a wide range of tasks while being lightweight enough to run on-device, and these humanized finetuned versions feel more human-like.",
    date: "2025-02",
    links: {
      report: "/research/smollm2-report",
      modelSeries: {
        name: "SmolLM2-Humanized Series",
        url: "https://huggingface.co/collections/AssistantsLab/smollm2-humanized-67b455b9e4cebfcd92d8d021"
      }
    },
    tags: ["Humanized AI", "Finetuned"]
  },
  {
    title: "Tiny-Toxic-Detector: A compact transformer-based model for toxic content detection",
    authors: ["Michiel Kamphuis"],
    abstract: "This paper presents Tiny-toxic-detector, a compact transformer-based model designed for toxic content detection. Despite having only 2.1 million parameters, Tiny-toxic-detector achieves competitive performance on benchmark datasets, rivaling models over 50 times its size.",
    date: "2024-08",
    links: {
      paper: "https://arxiv.org/abs/2409.02114",
      github: "https://github.com/AssistantsLab/tiny-toxic-detector",
      huggingface: "https://huggingface.co/AssistantsLab/tiny-toxic-detector"
    },
    tags: ["NLP", "Content Moderation", "Efficient ML"]
  }
];

const Research: React.FC = () => {
  const latestPaper = researchPapers[0];

  return (
    <div className="research-page">
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Research</h1>
            <p>Exploring the frontiers of AI assistance technology</p>
          </div>
        </div>
      </section>

      <section className="timeline-section">
        <div className="timeline">
          {researchPapers.map((paper, index) => (
            <div className="timeline-item" key={index}>
              <div className="timeline-dot"></div>
              <div className={`timeline-content ${index === 0 ? 'latest' : ''}`}>
                {index === 0 && <div className="latest-badge">Latest Item</div>}
                <div className="paper-card">
                  <h3>{paper.title}</h3>
                  <div className="paper-meta">
                    <div className="authors">{paper.authors.join(", ")}</div>
                    <div className="date">{paper.date}</div>
                  </div>
                  <p>{paper.abstract}</p>
                  <div className="tags">
                    {paper.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">{tag}</span>
                    ))}
                  </div>
                  <div className="paper-links">
                    {paper.links.paper && (
                      <a href={paper.links.paper} className="paper-link">
                        📄 Paper
                      </a>
                    )}
                    {paper.links.report && (
                      <a href={paper.links.report} className="paper-link">
                        📊 Report
                      </a>
                    )}
                    {paper.links.github && (
                      <a href={paper.links.github} className="paper-link">
                        <svg className="link-icon" viewBox="0 0 24 24">
                          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" fill="currentColor"/>
                        </svg>
                        GitHub
                      </a>
                    )}
                    {paper.links.huggingface && (
                      <a href={paper.links.huggingface} className="paper-link">
                        🤗 Model
                      </a>
                    )}
                    {paper.links.modelSeries && (
                      <a href={paper.links.modelSeries.url} className="paper-link model-series">
                        🤗 {paper.links.modelSeries.name}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Research;
